import { IconButton, styled } from '@ethings/ui-toolkit';
import { Toolbar } from '@mui/material';
import { FC, PropsWithChildren, RefObject, Suspense, type JSX } from 'react';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_WIDTH } from '../../constants';
import { useSmallScreen } from '../../hooks';
import { Icon } from '../icons';
import { MiddleSpinner } from '../middle-spinner';
import { AppDesktopLayout } from './layout-desktop';
import { AppMobileLayout } from './layout-mobile';
import { PageTitle } from './page-title';

interface Props extends PropsWithChildren {
  drawerContent: JSX.Element;
  drawerOpen: boolean;
  drawerRef?: RefObject<HTMLDivElement | undefined>;
  onDrawerClose: () => void;
  onDrawerOpen: () => void;
  siteTitle: string;
}

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

export const AppView: FC<Props> = ({ children, drawerContent, drawerOpen, drawerRef, onDrawerClose, onDrawerOpen, siteTitle }) => {
  const smallScreen = useSmallScreen();

  if (smallScreen) {
    return <AppMobileLayout
      drawerContent={drawerContent}
      drawerOpen={drawerOpen}
      drawerRef={drawerRef}
      onDrawerClose={onDrawerClose}
      onDrawerOpen={onDrawerOpen}
      toolbar={<Toolbar id="page-header" data-testid="mobile-toolbar">
        <StyledIconButton
          color="inherit"
          aria-label="toggle drawer"
          edge="start"
          onClick={drawerOpen ? onDrawerClose : onDrawerOpen}
        >
          <Icon icon="Menu" />
        </StyledIconButton>
        <PageTitle defaultValue={siteTitle} />
      </Toolbar>}
    >
      <Suspense fallback={<MiddleSpinner />}>
        {children}
      </Suspense>
    </AppMobileLayout>;
  }

  return (
    <AppDesktopLayout
      drawerContent={drawerContent}
      drawerOpen={drawerOpen}
      drawerRef={drawerRef}
      onDrawerClose={onDrawerClose}
      onDrawerOpen={onDrawerOpen}
      drawerWidthCollapsed={SIDEBAR_WIDTH}
      drawerWidthExpanded={SIDEBAR_FULL_WIDTH}
    >
      <Suspense fallback={<MiddleSpinner />}>
        {children}
      </Suspense>
    </AppDesktopLayout>
  );
};
